import { TErrorResponse } from '@lucidhq/lucidium';

export const ETAG_MISMATCH_ERROR_MESSAGE =
  'Update Failed: This item has just been modified by another user. Please refresh the page';

export const NAME_ALREADY_EXISTS_ERROR_MESSAGE =
  'This name already exists in this panel';

/**
 * Replaces backend error messages with the user facing copy defined by design.
 * Overridden messages are shown on their own, without the field name prefix.
 */
const ERROR_MESSAGE_OVERRIDES: { pattern: RegExp; message: string }[] = [
  {
    // covers both the open exchange and the private exchange duplicate name errors
    pattern: /incentive model with the same name already exists/i,
    message: NAME_ALREADY_EXISTS_ERROR_MESSAGE,
  },
];

const getOverriddenMessage = (message: string): string | undefined =>
  ERROR_MESSAGE_OVERRIDES.find(({ pattern }) => pattern.test(message))?.message;

interface ErrorsResponse {
  errors: {
    [key: string]: Array<{ message: string; code: string }>;
  };
}

type MaybeErrorResponse<T> = T extends ErrorsResponse ? T : undefined;

type ErrorObject = {
  [key: string]: string;
};

export type ErrorMessagesResult = (string | ErrorObject)[];

export const getErrorMessages = <T extends ErrorsResponse>(
  errorResponse: MaybeErrorResponse<T> | TErrorResponse | undefined,
  defaultErrorMessage: string = 'Something went wrong. Please try again later',
  statusCode?: number,
  includeCodeInErrorMessage: boolean = false
): ErrorMessagesResult => {
  // special handling for status 412 - etag mismatch
  if (statusCode === 412) {
    return [ETAG_MISMATCH_ERROR_MESSAGE];
  }

  const errors =
    errorResponse && 'errors' in errorResponse
      ? errorResponse.errors
      : undefined;

  if (errors && Object.keys(errors).length > 0 && statusCode !== 500) {
    return Object.entries(errors).flatMap(([key, errorArray]) =>
      errorArray.map((error) => {
        const overriddenMessage = getOverriddenMessage(error.message);

        if (overriddenMessage) return overriddenMessage;

        return includeCodeInErrorMessage && error.code
          ? `${key} ${error.code}: ${error.message}`
          : { [key]: error.message };
      })
    );
  }

  if (errorResponse && 'message' in errorResponse) {
    return [
      getOverriddenMessage(errorResponse.message) ?? errorResponse.message,
    ];
  }

  return [defaultErrorMessage];
};
